import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { localUser, login, validateMagicToken, loginWithPassword } from '@/services/session-service';
import { Button, LoadingOverlay, TextInput } from '@mantine/core';
import { ADMIN_HOME_PATH, CREATE_ACCOUNT_PATH } from '@/lib/navigation';
import logoUrl from '/images/favicon.svg';

export default function LoginPage(): JSX.Element {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [email, setEmail] = useState(searchParams.get('email') || '');
    const [password, setPassword] = useState('');
    const [loginMode, setLoginMode] = useState<'magic-link' | 'password'>('password');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const token = searchParams.get('token');

    // Handle token verification from email link
    useEffect(() => {
        if (token) {
            const verifyToken = async () => {
                setIsLoading(true);
                setError('');
                try {
                    await validateMagicToken(token);
                    navigate(ADMIN_HOME_PATH);
                } catch (err) {
                    setError((err as Error).message || 'Token verification failed. The link may have expired.');
                } finally {
                    setIsLoading(false);
                }
            };
            verifyToken();
        }
    }, [token, navigate]);

    // Check if user is already logged in
    useEffect(() => {
        if (localUser() && !token) {
            navigate(ADMIN_HOME_PATH);
        }
    }, [token, navigate]);

    const handleSubmit = async () => {
        if (!email) {
            setError('email-required');
            return;
        }
        if (loginMode === 'password' && !password) {
            setError('password-required');
            return;
        }
        setIsLoading(true);
        setError('');
        setSuccessMessage('');
        try {
            if (loginMode === 'magic-link') {
                await login(email);
                setSuccessMessage('magic-link-sent-description');
            } else {
                await loginWithPassword(email, password);
                navigate(ADMIN_HOME_PATH);
            }
        } catch (err) {
            setError((err as Error).message || 'Failed to sign in. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (email && !token) {
            setSearchParams({ email });
        } else if (!token) {
            setSearchParams({});
        }
    }, [email, token, setSearchParams]);

    return (
        <div className="min-h-screen flex flex-col relative">
            <LoadingOverlay visible={isLoading} loaderProps={{ size: 'md', color: 'black' }} />

            {/* Logo in top left */}
            <div className="p-6 flex items-end gap-2">
                <img src={logoUrl} className="w-10" alt="Logo" />
                <div className="text-3xl font-semibold text-gray-900">
                    Turizi
                </div>
            </div>

            {/* Centered login form */}
            <div className="flex-1 flex items-center justify-center">
                <div className="w-full max-w-md px-8">
                    <div className="space-y-6">
                        {token ? (
                            <div className="text-center py-8">
                                <h1 className="text-2xl font-normal text-gray-900 mb-2">
                                    {t('verifying_session')}
                                </h1>
                                <p className="text-sm text-gray-600">
                                    {t('verifying_session_desc')}
                                </p>
                            </div>
                        ) : (
                            <>
                                <div>
                                    <h1 className="text-2xl font-normal text-gray-900">
                                        {t('login')}
                                    </h1>
                                    <p className="text-sm text-gray-600 mt-2">
                                        {t('magic_link_description')}
                                    </p>
                                </div>

                                {error && (
                                    <div className="p-4 text-sm text-red-800 rounded-lg bg-red-50 border border-red-200">
                                        {t(error)}
                                    </div>
                                )}

                                {successMessage && (
                                    <div className="p-4 text-sm text-emerald-800 rounded-lg bg-emerald-50 border border-emerald-200">
                                        {t(successMessage)}
                                    </div>
                                )}

                                <div className="space-y-4">
                                    <TextInput
                                        label={t('email')}
                                        autoFocus
                                        id="email"
                                        name="email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handleSubmit();
                                            }
                                        }}
                                        autoComplete="email"
                                        size="lg"
                                        placeholder="you@example.com"
                                    />
                                    {loginMode === 'password' && (
                                        <TextInput
                                            label={t('password')}
                                            id="password"
                                            name="password"
                                            type="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleSubmit();
                                                }
                                            }}
                                            autoComplete="current-password"
                                            size="lg"
                                            placeholder="••••••••"
                                        />
                                    )}
                                    <div className="pt-2">
                                        <Button
                                            fullWidth
                                            size="md"
                                            color="black"
                                            onClick={handleSubmit}
                                        >
                                            {loginMode === 'magic-link' ? t('send_magic_link') : t('login')}
                                        </Button>
                                    </div>
                                    <div className="text-center">
                                        <Button
                                            variant="subtle"
                                            color="gray"
                                            size="xs"
                                            onClick={() => {
                                                setLoginMode(loginMode === 'magic-link' ? 'password' : 'magic-link');
                                                setError('');
                                            }}
                                        >
                                            {loginMode === 'magic-link'
                                                ? t('use_password_login')
                                                : t('use_magic_link')}
                                        </Button>
                                    </div>
                                    <div className="text-center pt-2 border-t border-gray-100">
                                        <Link
                                            to={CREATE_ACCOUNT_PATH}
                                            className="text-sm font-semibold text-gray-500 hover:text-black transition-colors"
                                        >
                                            {t('create_account_link')}
                                        </Link>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
